import { vendorTypeColors } from "./constants";
import {
  DOCUMENT_RISK_SCORE_FILTER_ENABLED,
  DOCUMENT_RISK_SCORE_FILTER_OPTIONS,
  mergeMyVendorKnownTags,
} from "./myVendorsConstants";
import { extractVendorListMembers, collectServiceNamesFromList } from "./myVendorListsUtils";

/**
 * Release gate for the rebuilt My Vendors UI.
 * false → legacy Hypercomply iframe at /Vendors; hides new affordances on Discovery etc.
 * true → MyVendorsPage and cross-page My Vendors actions (lists, bookmarks, bulk add).
 */
export const NEW_MY_VENDORS_UI_ENABLED = true;

/** Gate for new My Vendors features outside the /Vendors routes (e.g. Discovery). */
export function canUseNewMyVendorsAffordances(hasMyVendorsRight) {
  return NEW_MY_VENDORS_UI_ENABLED && Boolean(hasMyVendorsRight);
}

/** Canonical sponsor My Vendors route (replaces legacy HC iframe at same path). */
export const MY_VENDORS_ROUTE = "/Vendors";

/** Page key for Redux search text — update in one place if renamed. */
export const MY_VENDORS_PAGE_KEY = "myVendors";

/** Route guard right — swap when a dedicated My Vendors permission exists. */
export const MY_VENDORS_REQUIRED_RIGHT = "My Vendors";

export const MY_VENDORS_TAB_MINE = "mine";
export const MY_VENDORS_TAB_LISTS = "lists";

export const PLATFORM_MANUAL_FILTER_OPTIONS = [
  { value: "requested-in-platform", label: "Requested in Platform" },
  { value: "added-manually", label: "Added Manually" },
];

const GXP_CHIP_CLASSES = {
  GCP: "bg-slate-100 text-slate-700 ring-slate-600/20",
  GLP: "bg-slate-100 text-slate-700 ring-slate-600/20",
  GMP: "bg-slate-100 text-slate-700 ring-slate-600/20",
  GVP: "bg-slate-100 text-slate-700 ring-slate-600/20",
};

const ALL_GXPS = [
  "GCP",
  "GLP",
  "GMP",
  "GVP",
  "GAMP",
  "GCDMP",
  "GDocP",
  "GDP",
  "GEP",
  "GMLP",
  "GPP",
  "GSP",
  "GTP",
  "GTraP/GTrP",
];

/** GxP options for PageToolbarFilters. */
export const MY_VENDORS_GXP_FILTER_OPTIONS = ALL_GXPS.map((value) => ({
  value,
  label: value,
  chipClass:
    GXP_CHIP_CLASSES[value] || "bg-slate-100 text-slate-600 ring-slate-500/20",
}));

export function buildVendorTypeFilterOptions(providerTypes = []) {
  if (!providerTypes?.length) return [];
  return providerTypes.map((type) => ({
    value: type.id ?? type.value ?? type.name,
    label: type.name ?? type.label,
    dotColorClass: vendorTypeColors[type.name] || "bg-gray-400",
  }));
}

/**
 * My Vendors tab filter sections. Empty option arrays are omitted by the caller (empty-section suppression).
 */
export function buildMyVendorsFilterSections({
  providerTypes = [],
  v2ServiceOptions = [],
  allKnownTags = [],
  includeBookmarked = false,
}) {
  const sections = [];
  const knownTags = mergeMyVendorKnownTags(allKnownTags);

  const typeOptions = buildVendorTypeFilterOptions(providerTypes);
  if (typeOptions.length) {
    sections.push({
      id: "type",
      name: "Vendor Type",
      searchable: false,
      options: typeOptions,
      total: typeOptions.length,
    });
  }

  sections.push({
    id: "platformManual",
    name: "Platform / Manual",
    searchable: false,
    options: PLATFORM_MANUAL_FILTER_OPTIONS,
    total: PLATFORM_MANUAL_FILTER_OPTIONS.length,
  });

  if (v2ServiceOptions?.length) {
    sections.push({
      id: "services",
      name: "Services",
      searchable: true,
      isV2: true,
      options: v2ServiceOptions,
      total: v2ServiceOptions.length,
    });
  }

  if (MY_VENDORS_GXP_FILTER_OPTIONS.length) {
    sections.push({
      id: "gxp",
      name: "GxPs",
      searchable: true,
      options: MY_VENDORS_GXP_FILTER_OPTIONS,
      total: MY_VENDORS_GXP_FILTER_OPTIONS.length,
    });
  }

  if (DOCUMENT_RISK_SCORE_FILTER_ENABLED) {
    sections.push({
      id: "riskScores",
      name: "Document Risk Score",
      searchable: false,
      options: DOCUMENT_RISK_SCORE_FILTER_OPTIONS,
      total: DOCUMENT_RISK_SCORE_FILTER_OPTIONS.length,
    });
  }

  if (knownTags.length) {
    sections.push({
      id: "customTags",
      name: "Custom Tags",
      searchable: true,
      options: knownTags.map((tag) => ({ value: tag, label: tag })),
      total: knownTags.length,
    });
  }

  // Prototype: only show when at least one My Vendors row is bookmarked.
  if (includeBookmarked) {
    sections.push({
      id: "bookmarked",
      name: "Bookmarked",
      searchable: false,
      options: [
        { value: "yes", label: "Bookmarked" },
        { value: "no", label: "Not bookmarked" },
      ],
      total: 2,
    });
  }

  return sections;
}

/**
 * Vendor names for the Vendor Lists "Vendors" filter.
 * Prefers list payload when present; falls back to My Vendors until lists API ships.
 */
export function collectVendorListFilterVendorNames(vendorLists = [], fallbackVendors = []) {
  const names = new Set();

  vendorLists.forEach((list) => {
    extractVendorListMembers(list).forEach((member) => {
      if (member.name) names.add(member.name);
    });
  });

  if (names.size === 0) {
    fallbackVendors.forEach((vendor) => {
      const name = vendor?.name;
      if (name) names.add(String(name).trim());
    });
  }

  return Array.from(names).sort((a, b) => a.localeCompare(b));
}

/** Service names for the Vendor Lists "Services" filter — union of all list members. */
export function collectVendorListFilterServiceNames(vendorLists = [], fallbackVendors = []) {
  const names = new Set();

  vendorLists.forEach((list) => {
    collectServiceNamesFromList(list).forEach((service) => names.add(service));
  });

  if (names.size === 0) {
    fallbackVendors.forEach((vendor) => {
      (vendor.serviceCategories || vendor.services || []).forEach((service) => {
        if (service) names.add(String(service).trim());
      });
    });
  }

  return Array.from(names).sort((a, b) => a.localeCompare(b));
}

/** Filter sections for the Vendor Lists tab. */
export function buildVendorListsFilterSections({
  allKnownTags = [],
  v2ServiceOptions = [],
  vendorLists = [],
  fallbackVendors = [],
} = {}) {
  const sections = [];

  const serviceNames = collectVendorListFilterServiceNames(vendorLists, fallbackVendors);
  if (serviceNames.length) {
    sections.push({
      id: "services",
      name: "Services",
      searchable: true,
      options: serviceNames.map((name) => ({ value: name, label: name })),
      total: serviceNames.length,
    });
  } else if (v2ServiceOptions?.length) {
    sections.push({
      id: "services",
      name: "Services",
      searchable: true,
      isV2: true,
      options: v2ServiceOptions,
      total: v2ServiceOptions.length,
    });
  }

  const knownTags = mergeMyVendorKnownTags(allKnownTags);
  if (knownTags.length) {
    sections.push({
      id: "customTags",
      name: "Custom Tags",
      searchable: true,
      options: knownTags.map((tag) => ({ value: tag, label: tag })),
      total: knownTags.length,
    });
  }

  const vendorNames = collectVendorListFilterVendorNames(vendorLists, fallbackVendors);
  if (vendorNames.length) {
    sections.push({
      id: "vendors",
      name: "Vendors",
      searchable: true,
      options: vendorNames.map((name) => ({ value: name, label: name })),
      total: vendorNames.length,
    });
  }

  return sections;
}

export const EMPTY_MY_VENDORS_SELECTED_IDS = {
  type: [],
  platformManual: [],
  services: [],
  gxp: [],
  riskScores: [],
  customTags: [],
  bookmarked: [],
  vendors: [],
};
